import React, { FC, useState } from "react";
import VODAdminLayout from "../../../layouts/admin/VODAdminLayout";
import { NavLink, useParams } from "react-router";
import {
    useExportUpdateVodContent,
    useGetContent,
    useGetContentGroup,
    useGetFpbSummary,
} from "../../../services/vod/Query";
import {
    AudioType,
    Bookmark,
    Content,
    ContentGroup,
    ContentTypeLabels,
    Image,
    ImageOrientationLabels,
    ImagePurposeLabels,
    ImageSizeLabels,
    PreviewVideoAsset,
    VideoAsset,
    VideoType,
} from "../../../models/vod/Models";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faCalendarDays,
    faChevronRight,
    faFilm,
    faImages,
    faLink,
    faRectangleList,
    faRectangleAd,
    faBookBookmark,
    faUpRightFromSquare,
    faPenToSquare,
    faGavel,
} from "@fortawesome/free-solid-svg-icons";
import Card from "../../../components/themed/Card";
import { toCountryName, toLanguageName } from "../../../const";
import dayjs from "dayjs";
import DashboardImage from "../../../components/DashboardImage";
import ContentModal from "./components/form/ContentModal";
import ExportModal from "./components/modal/ExportModal";
import FpbSummaryModal from "./components/form/FpbSummaryModal";
import FileUpload from "./components/FileUpload";
import PreviewVideoAssetFormModal from "./components/form/PreviewVideoAssetFormModal";
import VideoAssetFormModal from "./components/form/VideoAssetFormModal";
import OfferWindowsForm from "./components/form/OfferWindowsForm";

const VODContentView: FC = () => {
    const {
        boxSetAmsProviderId,
        boxSetBaseContentGroupId,
        seriesAmsProviderId,
        seriesBaseContentGroupId,
        seasonAmsProviderId,
        seasonBaseContentGroupId,
        amsProviderId,
        baseContentId,
    } = useParams();

    const { data: boxSetContentGroup } =
        useGetContentGroup(boxSetAmsProviderId ?? "", boxSetBaseContentGroupId ?? "", !!boxSetAmsProviderId && !!boxSetBaseContentGroupId);

    const { data: seriesContentGroup } =
        useGetContentGroup(seriesAmsProviderId ?? "", seriesBaseContentGroupId ?? "", !!seriesAmsProviderId && !!seriesBaseContentGroupId);

    const { data: seasonContentGroup } =
        useGetContentGroup(seasonAmsProviderId ?? "", seasonBaseContentGroupId ?? "", !!seasonAmsProviderId && !!seasonBaseContentGroupId);

    const { data: content } =
        useGetContent(amsProviderId ?? "", baseContentId ?? "", !!amsProviderId && !!baseContentId);
    const exportUpdateVodContent = useExportUpdateVodContent();

    const { data: fpbSummary } = useGetFpbSummary(content && content.id ? content.id : "");

    const [ openEditFpbSummaryModal, setOpenEditFpbSummaryModal ] = useState(false);
    const [ openPreviewVideoAssetEditModal,  setPreviewVideoAssetEditModal ] = useState(false);
    const [ openVideoAssetEditModal,  setVideoAssetEditModal ] = useState(false);
    const [ openOfferWindowsEditModal,  setOpenOfferWindowsEditModal ] = useState(false);
    const [ openOfferWindowsExportModal, setOpenOfferWindowsExportModal ] = useState(false);


    const openModal = (
        e: React.MouseEvent<HTMLElement, MouseEvent>,
        setOpenModal: (openModal: boolean) => void,
    ) => {
        e.stopPropagation();
        setOpenModal(true);
    };

    const closeModal = (
        setOpenModal: (openModal: boolean) => void
    ) => {
        setOpenModal(false);
    };

    const renderEpisodicNavigation = (
        boxSetContentGroup: ContentGroup | undefined,
        seriesContentGroup: ContentGroup | undefined,
        seasonContentGroup: ContentGroup | undefined,
        content: Content | undefined,
    ) => {
        const boxSetNavLink = boxSetContentGroup ? `/box-set-content-group/${boxSetContentGroup.id}` : "";
        const seriesNavLink = seriesContentGroup ? `/series-content-group/${seriesContentGroup.id}` : "";
        const seasonNavLink = seasonContentGroup ? `/season-content-group/${seasonContentGroup.id}` : "";
        const contentNavLinkBase = `${boxSetNavLink}${seriesNavLink}${seasonNavLink}`;
        const contentNavLink =
            content
            ? (contentNavLinkBase ? `${contentNavLinkBase}/content/${content.id}` : `/standalone-content/${content.id}`)
            : "";

        return (
            <Card className="px-6 py-4">
                <div className="flex justify-between">
                    <div className="flex flex-row flex-wrap">
                        {boxSetContentGroup && boxSetNavLink && (
                            <>
                                <NavLink
                                    to={`/dashboard/vod/assets/box-set-content-group`}
                                >
                                    VOD Assets
                                </NavLink>
                                <FontAwesomeIcon icon={faChevronRight} className="py-1 mx-4" />
                                <NavLink
                                    to={`/dashboard/vod/assets${boxSetNavLink}`}
                                >
                                    {boxSetContentGroup.title}
                                </NavLink>
                            </>
                        )}
                        {seriesContentGroup && seriesNavLink && (
                            <>
                                {boxSetContentGroup ? (
                                    <>
                                        <FontAwesomeIcon icon={faChevronRight} className="py-1 mx-4" />
                                        <NavLink
                                            to={`/dashboard/vod/assets${boxSetNavLink}${seriesNavLink}`}
                                        >
                                            {seriesContentGroup.title}
                                        </NavLink>
                                    </>
                                ) : (
                                    <>
                                        <NavLink
                                            to={`/dashboard/vod/assets/series-content-group`}
                                        >
                                            VOD Assets
                                        </NavLink>
                                        <FontAwesomeIcon icon={faChevronRight} className="py-1 mx-4" />
                                        <NavLink
                                            to={`/dashboard/vod/assets${seriesNavLink}`}
                                        >
                                            {seriesContentGroup.title}
                                        </NavLink>
                                    </>
                                )}
                            </>
                        )}
                        {seasonContentGroup && seasonNavLink && (
                            <>
                                <FontAwesomeIcon icon={faChevronRight} className="py-1 mx-4" />
                                <NavLink
                                    to={`/dashboard/vod/assets${boxSetNavLink}${seriesNavLink}${seasonNavLink}`}
                                >
                                    {seasonContentGroup.title}
                                </NavLink>
                            </>
                        )}
                        {content && contentNavLink && (
                            <>
                                <FontAwesomeIcon icon={faChevronRight} className="py-1 mx-4" />
                                <NavLink
                                    to={`/dashboard/vod/assets${contentNavLink}`}
                                >
                                    {content.title}
                                </NavLink>
                            </>
                        )}
                    </div>
                    <div className={"flex gap-2"}>
                        <button
                            data-tooltip-content="Edit FPB Summary"
                            data-tooltip-id="global-tooltip"
                            onClick={(e) => openModal(e, setOpenEditFpbSummaryModal)}
                        >
                            <FontAwesomeIcon icon={faGavel} size="1x" className="text-brand-300 ml-4"/>
                        </button>
                        <button
                            data-tooltip-content="Synamedia Export"
                            data-tooltip-id="global-tooltip"
                            onClick={() => setOpenOfferWindowsExportModal(true)}
                        >
                            <FontAwesomeIcon icon={faUpRightFromSquare} size="1x" className="text-brand-300"/>
                        </button>
                    </div>
                </div>
            </Card>
        );
    };


    const renderStandaloneNavigation = (
        boxSetContentGroup: ContentGroup | undefined,
        content: Content | undefined,
    ) => {
        const boxSetNavLink = boxSetContentGroup ? `/box-set-content-group/${boxSetContentGroup.id}` : "";
        const contentNavLinkBase = `${boxSetNavLink}`;
        const contentNavLink =
            content
                ? (contentNavLinkBase ? `${contentNavLinkBase}/content/${content.id}` : `/standalone-content/${content.id}`)
                : "";

        return (
            <>
                <Card className="px-6 py-4">
                    <div className="flex justify-between">
                        <div className="flex flex-row flex-wrap">
                            {content && contentNavLink && (
                                <>
                                    {(boxSetContentGroup && boxSetNavLink) ? (
                                        <>
                                            <NavLink
                                                to={`/dashboard/vod/assets/box-set-content-group`}
                                            >
                                                VOD Assets
                                            </NavLink>
                                            <FontAwesomeIcon icon={faChevronRight} className="py-1 mx-4" />
                                            <NavLink
                                                to={`/dashboard/vod/assets${boxSetNavLink}`}
                                            >
                                                {boxSetContentGroup.title}
                                            </NavLink>
                                        </>
                                    ) : (
                                        <NavLink
                                            to={`/dashboard/vod/assets/standalone-content`}
                                        >
                                            VOD Assets
                                        </NavLink>
                                    )}
                                    <FontAwesomeIcon icon={faChevronRight} className="py-1 mx-4" />
                                    <NavLink
                                        to={`/dashboard/vod/assets${contentNavLink}`}
                                    >
                                        {content.title}
                                    </NavLink>
                                </>
                            )}
                        </div>
                        <div className={"flex gap-2"}>
                            <button
                                data-tooltip-content="Edit FPB Summary"
                                data-tooltip-id="global-tooltip"
                                onClick={(e) => openModal(e, setOpenEditFpbSummaryModal)}
                            >
                                <FontAwesomeIcon icon={faGavel} size="1x" className="text-brand-300 ml-4"/>
                            </button>
                            {
                                content && content.offerWindows && content.offerWindows.length > 0 &&
                                <button
                                    data-tooltip-content="Synamedia Export"
                                    data-tooltip-id="global-tooltip"
                                    onClick={() => setOpenOfferWindowsExportModal(true)}
                                >
                                    <FontAwesomeIcon icon={faUpRightFromSquare} size="1x" className="text-brand-300"/>
                                </button>
                            }
                        </div>
                    </div>
                </Card>
            </>
        );
    };

    const renderNavigation = (
        boxSetContentGroup: ContentGroup | undefined,
        seriesContentGroup: ContentGroup | undefined,
        seasonContentGroup: ContentGroup | undefined,
        content: Content | undefined,
    ) => {
        if (!content || !content.contentType) return (<></>);

        return (
            <>
                {
                    content.contentType === "STANDALONE" ?
                        renderStandaloneNavigation(boxSetContentGroup, content) :
                    content.contentType === "EPISODE" ?
                        renderEpisodicNavigation(boxSetContentGroup, seriesContentGroup, seasonContentGroup, content)
                        : <></>
                }
                {
                    content.id && content.offerWindows && content.offerWindows.length > 0 &&
                    <ExportModal
                        mutateFunc={exportUpdateVodContent.mutate}
                        id = {content.id}
                        offerWindows={content.offerWindows}
                        title ={`${content.id} - ${content.title}`}
                        modalId={"export-update-content"}
                        openModal={openOfferWindowsExportModal}
                        onClose={() => setOpenOfferWindowsExportModal(false)}
                    />
                }
                {
                    fpbSummary &&
                    <FpbSummaryModal
                        openModal = {openEditFpbSummaryModal}
                        onClose = {() => closeModal(setOpenEditFpbSummaryModal)}
                        key = {fpbSummary.itemId}
                        modalId = {"edit-modal-fpb"}
                        content = {fpbSummary}
                        onlyCertificateFields
                    />
                }
            </>
        );
    };

    const badge = (item: string, colourVariant: string = "brand") => {
        return (
            <div key={item.toLowerCase().replace(/^0-9a-z/, "_")}
                 className={`py-1 px-2 text-xs text-${colourVariant}-900 bg-${colourVariant}-300 dark:text-white dark:bg-${colourVariant}-500 font-bold rounded-full`}
            >
                {item}
            </div>
        );
    };
    const badgeList = (items: string[] | null | undefined, colourVariant: string = "brand", emptyListValue: string = "None") => {
        return (
            <div className="flex flex-row flex-wrap gap-1">
                {
                    items?.length
                    ? items.filter(item => !!item).map(item => badge(item, colourVariant))
                    : (emptyListValue && badge(emptyListValue, "red"))
                }
            </div>
        );
    };
    const renderMainMetadata = (
        seriesContentGroup: ContentGroup | undefined,
        seasonContentGroup: ContentGroup | undefined,
        content: Content | undefined,
    ) => {
        // TODO Languages
        if (!content) return <></>;

        return (
            <>
                <Card className="px-8 py-4">
                    <div className="pb-2 mb-4 border-b-2 border-b-brand-500 dark:border-b-white flex flex-row items-center justify-between">
                        <div className={`text-lg flex flex-row flex-wrap items-center`}>
                            {content && <FontAwesomeIcon icon={faRectangleList} className="mx-4"/>}
                            {
                                seriesContentGroup &&
                                <div className="font-bold">{seriesContentGroup.title}</div>
                            }
                            {
                                seasonContentGroup &&
                                <div className="font-bold">
                                    <FontAwesomeIcon icon={faChevronRight} className="mx-4"/>
                                    {seasonContentGroup.title}
                                </div>
                            }
                            {
                                <div className="font-bold">
                                    {content.contentType !== "STANDALONE" && <FontAwesomeIcon icon={faChevronRight} className="mx-4"/>}
                                    {content.title}
                                </div>
                            }
                            {
                                content.contentType !== "STANDALONE" &&
                                <div className="font-bold flex flex-row text-gray-500 ml-4">
                                    {!!seasonContentGroup?.sequenceNumber && !!content?.sequenceNumber ?
                                        `S${seasonContentGroup.sequenceNumber} E${content.sequenceNumber}` : ""}
                                </div>
                            }
                            <span className="mx-4 text-gray-500 text-sm">
                                    {content.contentType ? ContentTypeLabels[content.contentType] : ""}
                            </span>
                        </div>
                        <button
                            data-tooltip-content={`Edit ${content ?
                                `${content.contentType?.charAt(0)}${content.contentType?.substring(1).toLowerCase()}` : "Content Group"} Main Metadata`}
                            data-tooltip-id="global-tooltip"
                            command="show-modal" commandfor="edit-vod-content-main-metadata"
                        >
                            <FontAwesomeIcon icon={faPenToSquare} size="1x" className="text-brand-300"/>
                        </button>
                    </div>
                    <div className="flex flex-row flex-wrap mb-4">
                        <div className="flex flex-col flex-wrap gap-1 w-[50%]">
                            <label className="font-bold">Categories</label>
                            {badgeList(content.classification?.categories)}
                        </div>
                        <div className="flex flex-col flex-wrap gap-1 w-[50%]">
                            <label className="font-bold">Genres</label>
                            {badgeList(content.classification?.genres)}
                        </div>
                    </div>
                    <div className="mb-4">
                        {
                            content.summaries?.longSynopsis &&
                            <div>
                                <label className="font-bold">Long Synopsis</label>
                                <p className="text-sm text-justify">{content.summaries.longSynopsis}</p>
                            </div>
                        }
                        {
                            content.summaries?.mediumSynopsis &&
                            <div>
                                <label className="font-bold">Medium Synopsis</label>
                                <p className="text-sm text-justify">{content.summaries.mediumSynopsis}</p>
                            </div>
                        }
                        {
                            content.summaries?.shortSynopsis &&
                            <div>
                                <label className="font-bold">Short Synopsis</label>
                                <p className="text-sm text-justify">{content.summaries.shortSynopsis}</p>
                            </div>
                        }
                    </div>
                    <div className="flex flex-col gap-1 flex-wrap mb-4">
                        <label className="font-bold">Keywords</label>
                        {badgeList(content.classification?.keywords)}
                    </div>
                    <div className="flex flex-row flex-wrap justify-between items-end mb-4">
                        <div>
                            { !!content.releaseInfo?.yearOfRelease && badge(String(content.releaseInfo?.yearOfRelease), "gray") }
                        </div>
                        <div className="flex flex-row flex-wrap">
                            {
                                !!content.releaseInfo?.countriesOfOrigin?.length &&
                                badgeList(content.releaseInfo.countriesOfOrigin.map(countryCode => toCountryName(countryCode)), "gray")
                            }
                        </div>
                        <div className="flex flex-row flex-wrap">
                            {
                                !!content.videoAsset?.languages?.length &&
                                badgeList(content.videoAsset?.languages.map(languageCode => toLanguageName(languageCode)), "gray")
                            }
                        </div>
                        <div className="flex flex-col">
                            {
                                content.parentalRatings?.map(parentalRating =>
                                    <div key={`${parentalRating.region}_${parentalRating.system}`}>
                                        <label className="font-bold">
                                            <span className="font-bold mr-4">{parentalRating.system?.toUpperCase()}</span>
                                            <span className="text-sm text-gray-500">{parentalRating.region?.toUpperCase()}</span>
                                        </label>
                                        <div className="flex flex-row">
                                            <div className="mr-2">
                                                {parentalRating.label ? badge(parentalRating.label) : badge("Not Specified", "red")}
                                            </div>
                                            {!!parentalRating.advisories?.length && badgeList(parentalRating.advisories, "gray")}
                                        </div>
                                    </div>
                                )
                            }
                        </div>
                    </div>
                    <div className="flex flex-col mb-4">
                        {
                            !!content.castAndCrew?.actors?.filter(t => !!t)?.length &&
                            <div>
                                <label className="font-bold">Actors</label>
                                <p className="text-sm">
                                    {content.castAndCrew.actors.map(actor => actor.split(",").reverse().join(" ")).join(", ")}
                                </p>
                            </div>
                        }
                        {
                            !!content.castAndCrew?.directors?.filter(t => !!t)?.length &&
                            <div>
                                <label className="font-bold">Directors</label>
                                <p className="text-sm">
                                    {content.castAndCrew.directors.map(director => director.split(",").reverse().join(" ")).join(", ")}
                                </p>
                            </div>
                        }
                        {
                            !!content.castAndCrew?.producers?.filter(t => !!t)?.length &&
                            <div>
                                <label className="font-bold">Producers</label>
                                <p className="text-sm">
                                    {content.castAndCrew.producers.map(producer => producer.split(",").reverse().join(" ")).join(", ")}
                                </p>
                            </div>
                        }
                    </div>
                </Card>
                {
                    content &&
                    <ContentModal content={content} modalId={"edit-vod-content-main-metadata"}/>
                }
            </>
        );
    };

    const renderReferences = (content: Content) => {
        return (
            <Card className="px-8 py-4">
                <h2 className="text-lg pb-2 mb-4 border-b-2 border-b-brand-500 dark:border-b-white">
                    <span className="font-bold mr-6">
                        <FontAwesomeIcon icon={faLink} className="mx-4"/>
                        References
                    </span>
                </h2>
                <div className="grid grid-cols-2 grid-rows-[auto] gap-x-4 gap-y-2 items-center">
                    <div className="text-right font-bold">Id</div>
                    <div className="text-sm">{content.id}</div>
                    <div className="text-right font-bold">Provider Asset Id</div>
                    <div className="text-sm">{content.providerAssetId}</div>
                    <div className="text-right font-bold">Aggregator Id</div>
                    <div className="text-sm">{content.providerId}</div>
                    <div className="text-right font-bold">Aggregator Name</div>
                    <div className="text-sm">{content.providerName}</div>
                    <div className="text-right font-bold">Content Partner Id</div>
                    <div className="text-sm">{content.contentPartnerId}</div>
                    <div className="text-right font-bold">Content Partner Name</div>
                    <div className="text-sm">{content.contentPartnerName}</div>
                    <div className="text-right font-bold">Product</div>
                    <div className="text-sm">{content.product}</div>
                    <div className="text-right font-bold">Created</div>
                    <div className="text-sm">{dayjs(content.createdDateTime).format("DD MMM YYYY [@] HH:mm")}</div>
                    <div className="text-right font-bold">Last Modified</div>
                    <div className="text-sm">{dayjs(content.lastModifiedDateTime).format("DD MMM YYYY [@] HH:mm")}</div>
                    <div className="text-right font-bold">Version</div>
                    <div className="text-sm">{content.version}</div>
                </div>
            </Card>
        );
    };

    const renderScheduling = (content: Content) => {
        return (
            <Card className="px-8 py-4">
                <div className="flex justify-between text-lg pb-2 mb-4 border-b-2 border-b-brand-500 dark:border-b-white">
                    <span className="font-bold mr-6">
                        <FontAwesomeIcon icon={faCalendarDays} className="mx-4"/>
                        Scheduling
                    </span>
                    <button onClick={() => setOpenOfferWindowsEditModal(true)}>
                        <FontAwesomeIcon icon={faPenToSquare} size="1x" className="text-brand-300"/>
                    </button>
                </div>
                <div className="flex flex-row gap-8 justify-start mb-8">
                    <div className="flex flex-col gap-1 flex-wrap">
                        <label className="font-bold">License Window Start</label>
                        <span className="text-sm">
                        {
                            content.licensingWindow?.startDateTime
                            ? dayjs(content.licensingWindow.startDateTime).format("DD MMM YYYY [@] HH:mm")
                            : ""
                        }
                        </span>
                    </div>
                    <div className="flex flex-col gap-1 flex-wrap">
                        <label className="font-bold">License Window End</label>
                        <span className="text-sm">
                        {
                            content.licensingWindow?.endDateTime
                            ? dayjs(content.licensingWindow.endDateTime).format("DD MMM YYYY [@] HH:mm")
                            : ""
                        }
                        </span>
                    </div>
                </div>
                {
                    content.offerWindows?.length
                    ? <div className="mb-4">
                        <div className="mb-2">
                            <label className="font-bold">Offer Windows</label>
                        </div>
                        <div className="w-full">
                            <table className="w-full table-auto">
                                <colgroup>
                                    <col className="w-1"/>
                                    <col className="w-1"/>
                                    <col className="w-1"/>
                                    <col/>
                                    <col/>
                                    <col className="w-1"/>
                                    <col className="w-1"/>
                                </colgroup>
                                <thead
                                    className="text-white dark:text-white forced-colors:text-[HighlightText] bg-gray-800 dark:bg-gray-800 forced-colors:bg-[Highlight]"
                                >
                                    <tr>
                                        <th className="px-4 py-1 align-top text-left text-nowrap">Type</th>
                                        <th className="px-4 py-1 align-top text-left text-nowrap">Tenant</th>
                                        <th className="px-4 py-1 align-top text-left text-nowrap">DEP Code / Product</th>
                                        <th className="px-4 py-1 align-top text-left text-nowrap">Description</th>
                                        <th className="px-4 py-1 align-top text-left text-nowrap">Catalogues</th>
                                        <th className="px-4 py-1 align-top text-left text-nowrap">Start</th>
                                        <th className="px-4 py-1 align-top text-left text-nowrap">End</th>
                                    </tr>
                                </thead>
                                <tbody>
                                {
                                    content.offerWindows.map((offerWindow) => {
                                        return (
                                            <tr className="border-t hover:bg-brand-400 hover:text-white"
                                                key={`${offerWindow.offerType}_${offerWindow.name}_${offerWindow.tenantId}_${offerWindow.catalogues?.join(",")}_${offerWindow.depCode}`}
                                            >
                                                <td className="px-4 py-1 align-top text-sm text-nowrap">
                                                    {offerWindow.offerType}
                                                </td>
                                                <td className="px-4 py-1 align-top text-sm text-nowrap">
                                                    {offerWindow.tenantId}
                                                </td>
                                                <td className="px-4 py-1 align-top text-nowrap">

                                                    {
                                                        offerWindow.offerType === "SVOD" || offerWindow.offerType === "AVOD"
                                                        ? badgeList((offerWindow.productName ? [ offerWindow.productName ] : []), "green")
                                                        : badgeList((offerWindow.depCode ? [ offerWindow.depCode ] : []), "green")
                                                    }
                                                </td>
                                                <td className="px-4 py-1 align-top text-sm text-nowrap">
                                                    {offerWindow.displayDescription ?? ""}
                                                </td>
                                                <td className="px-4 py-1 align-top text-nowrap">
                                                    {badgeList(offerWindow.catalogues?.flatMap(c => (c.id ? [ c.id ] : [])) ?? [])}
                                                </td>
                                                <td className="px-4 py-1 align-top text-sm text-nowrap">
                                                    {!!offerWindow.startDateTime && dayjs(offerWindow.startDateTime).format("YYYY-MM-DD HH:mm")}
                                                </td>
                                                <td className="px-4 py-1 align-top text-sm text-nowrap">
                                                    {!!offerWindow.endDateTime && dayjs(offerWindow.endDateTime).format("YYYY-MM-DD HH:mm")}
                                                </td>
                                            </tr>
                                        );
                                    })
                                }
                                </tbody>
                            </table>
                        </div>
                      </div>
                    : <></>
                }
                <OfferWindowsForm
                    openModal={openOfferWindowsEditModal}
                    onClose={() => setOpenOfferWindowsEditModal(false)}
                    content={content}
                />
            </Card>
        );
    };

    const renderImage = (image?: Image | null, deviceLabel?: string | null) => {
        return (
            <>
                <DashboardImage
                    description={""}
                    src={`/api/images/?url=${encodeURIComponent(image?.url ?? "")}`}
                />
                <div className="text-xs text-center mt-2">
                {
                    <span>
                    {
                        `${!!image?.size && !!ImageSizeLabels[image.size] ? ImageSizeLabels[image.size] : ""} ` +
                        `${!!image?.orientation && !!ImageOrientationLabels[image.orientation] ? ImageOrientationLabels[image.orientation] : ""}`
                    }
                    </span>
                }
                {
                    <span className="mx-2">|</span>
                }
                {
                    image?.purpose && ImagePurposeLabels[image.purpose] &&
                    <span>{`${deviceLabel ?? ""} ${ImagePurposeLabels[image.purpose]}`}</span>
                }
                </div>
                <div className="text-xs text-center mt-2">
                    {`${image?.width ?? "?"}px x ${image?.height ?? "?"}px`}
                </div>
            </>
        );
    };
    const renderImages = (content: Content) => {
        return (
            <Card className="px-8 py-4">
                <h2 className="text-lg pb-2 mb-4 border-b-2 border-b-brand-500 dark:border-b-white">
                    <span className="font-bold mr-6">
                        <FontAwesomeIcon icon={faImages} className="mx-4"/>
                        Images
                    </span>
                </h2>
                <div className="grid grid-cols-5 grid-rows-[auto] items-center gap-4">
                    <div className="col-span-5 flex gap-2 items-center">
                        <label className="font-bold">Landscape Posters</label>
                        <FileUpload
                            asset={content}
                            imageEntityType={"POSTER_LANDSCAPE"}
                        />
                    </div>
                    <div className="row-start-2">
                        {renderImage(content?.images?.POSTER_LANDSCAPE_STANDARD_HERO_SMALL, "Low-Res")}
                    </div>
                    <div className="row-start-2">
                        {renderImage(content?.images?.POSTER_LANDSCAPE_STANDARD_HERO_MEDIUM, "Low-Res")}
                    </div>
                    <div className="row-start-2">
                        {renderImage(content?.images?.POSTER_LANDSCAPE_WIDESCREEN_HERO_MEDIUM, "Low-Res")}
                    </div>
                    <div className="row-start-2">
                        {renderImage(content?.images?.POSTER_LANDSCAPE_WIDESCREEN_THUMBNAIL_MEDIUM, "High-Res")}
                    </div>
                    <div className="row-start-2">
                        {renderImage(content?.images?.POSTER_LANDSCAPE_WIDESCREEN_HERO_LARGE, "High-Res")}
                    </div>

                    <div className="col-span-5 flex gap-2 items-center">
                        <label className="font-bold">Landscape Clear Posters</label>
                        <FileUpload
                            asset={content}
                            imageEntityType={"CLEAR_POSTER_LANDSCAPE"}
                        />
                    </div>
                    <div className="row-start-4">
                        {renderImage(content?.images?.CLEAR_POSTER_LANDSCAPE_STANDARD_HERO_SMALL, "Low-Res")}
                    </div>
                    <div className="row-start-4">
                        {renderImage(content?.images?.CLEAR_POSTER_LANDSCAPE_STANDARD_HERO_MEDIUM, "Low-Res")}
                    </div>
                    <div className="row-start-4">
                        {renderImage(content?.images?.CLEAR_POSTER_LANDSCAPE_WIDESCREEN_HERO_MEDIUM, "Low-Res")}
                    </div>
                    <div className="row-start-4">
                        {renderImage(content?.images?.CLEAR_POSTER_LANDSCAPE_WIDESCREEN_THUMBNAIL_MEDIUM, "High-Res")}
                    </div>
                    <div className="row-start-4">
                        {renderImage(content?.images?.CLEAR_POSTER_LANDSCAPE_WIDESCREEN_HERO_LARGE, "High-Res")}
                    </div>
                    <div className="col-span-3 flex gap-2 items-center">
                        <label className="font-bold">Portrait Posters</label>
                        <FileUpload
                            asset={content}
                            imageEntityType={"POSTER_PORTRAIT"}
                        />
                    </div>
                    <div className="col-span-2 col-start-4 row-start-5 flex gap-2 items-center">
                        <label className="font-bold">Still Frames</label>
                        <FileUpload
                            asset={content}
                            imageEntityType={"STILL_FRAME_LANDSCAPE"}
                        />
                    </div>
                    <div className="row-start-6">
                        {renderImage(content?.images?.POSTER_PORTRAIT_THUMBNAIL_SMALL, "Low-Res")}
                    </div>
                    <div className="row-start-6">
                        {renderImage(content?.images?.POSTER_PORTRAIT_THUMBNAIL_MEDIUM, "Low-Res")}
                    </div>
                    <div className="row-start-6">
                        {renderImage(content?.images?.POSTER_PORTRAIT_THUMBNAIL_LARGE, "High-Res")}
                    </div>
                    <div className="row-start-6">
                        {renderImage(content?.images?.STILL_FRAME_LANDSCAPE_WIDESCREEN_HERO_MEDIUM, "Low-Res")}
                    </div>
                    <div className="row-start-6">
                        {renderImage(content?.images?.STILL_FRAME_LANDSCAPE_WIDESCREEN_HERO_LARGE, "High-Res")}
                    </div>
                </div>
            </Card>
        );
    };

    const renderVideoType = (videoType?: VideoType | null) => {
        switch(videoType) {
            case "UNKNOWN": return "Default (High Definition)";
            case "SD": return "Standard Definition";
            case "HD": return "High Definition";
            case "THREE_D": return "3D";
            case "UHD4K": return "Ultra High Definition (4K)";
            default: return <></>;
        }
    };

    const renderAudioType = (audioType?: AudioType | null) => {
        switch(audioType) {
            case "UNKNOWN": return "Default (Stereo)";
            case "MONO": return "Mono";
            case "STEREO": return "Stereo";
            case "SURROUND_SOUND": return "Surround Sound";
            case "DOLBY_DIGITAL_5_1": return "Dolby Digital 5.1";
            default: return <></>;
        }
    };

    const renderPreviewVideoAsset = (previewVideoAsset: PreviewVideoAsset) => {
        return (
            <div className="flex flex-col gap-4 mb-4 overflow-x-hidden" key = {previewVideoAsset.url}>
                <div className="text-sm text-nowrap text-ellipsis overflow-x-hidden">{previewVideoAsset.url ?? ""}</div>
                <div className="flex flex-row justify-between text-xs">
                    <div>{renderVideoType(previewVideoAsset.videoType)}</div>
                    <div>{renderAudioType(previewVideoAsset.audioType)}</div>
                    <div>{previewVideoAsset.runTime && badge(dayjs.duration(previewVideoAsset.runTime).format("HH:mm:ss"))}</div>
                </div>
            </div>
        );
    };

    const renderPreviewVideoAssets = (content: Content) => {
        return (
            <Card className="px-8 py-4">
                <div className="flex justify-between text-lg pb-2 mb-4 border-b-2 border-b-brand-500 dark:border-b-white">
                    <span className="font-bold mr-6">
                        <FontAwesomeIcon icon={faFilm} className="mx-4"/>
                        Preview Video Assets
                    </span>
                    {
                        content.previewVideoAssets && content.previewVideoAssets.length > 0  &&
                        <>
                            <button
                                data-tooltip-content="Edit Preview Video Assets"
                                data-tooltip-id="global-tooltip"
                                onClick={() => setPreviewVideoAssetEditModal(true)}
                            >
                                <FontAwesomeIcon icon={faPenToSquare} size="1x" className="text-brand-300"/>
                            </button>
                            <PreviewVideoAssetFormModal
                                modalId={`previewVideoAssetModal-${content.id}`}
                                openModal={openPreviewVideoAssetEditModal}
                                onClose={() => setPreviewVideoAssetEditModal(false)}
                                content={content}
                            />
                        </>
                    }
                </div>
                {
                    !content.previewVideoAssets?.length ?
                    <div className={`w-full h-full flex justify-center items-center`}>
                        <p className={`text-gray-500 font-bold py-6`}>Not Available</p>
                    </div> :
                    content.previewVideoAssets
                    ?.sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
                    ?.map(previewVideoAsset => renderPreviewVideoAsset(previewVideoAsset))
                }
            </Card>
        );
    };

    const renderVideoAsset = (videoAsset: VideoAsset) => {
        return (
            <div className="flex flex-col gap-4 mb-4 overflow-x-hidden">
                <div className="text-sm text-nowrap text-ellipsis overflow-x-hidden">{videoAsset.url ?? ""}</div>
                <div className="flex flex-row justify-between text-xs">
                    <div>{renderVideoType(videoAsset.videoType)}</div>
                    <div>{renderAudioType(videoAsset.audioType)}</div>
                    <div>{videoAsset.runTime && badge(dayjs.duration(videoAsset.runTime).format("HH:mm:ss"))}</div>
                </div>
            </div>
        );
    };

    const renderVideoAssets = (content: Content) => {
        // TODO Languages
        if (!content.videoAsset) return (<></>);

        return (
            <Card className="px-8 py-4">
                <div className="flex justify-between text-lg pb-2 mb-4 border-b-2 border-b-brand-500 dark:border-b-white">
                    <span className="font-bold mr-6">
                        <FontAwesomeIcon icon={faFilm} className="mx-4"/>
                        Video Assets
                    </span>
                    {
                        content.videoAsset &&
                        <>
                            <button
                                data-tooltip-content="Edit Video Asset"
                                data-tooltip-id="global-tooltip"
                                onClick={() => setVideoAssetEditModal(true)}
                            >
                                <FontAwesomeIcon icon={faPenToSquare} size="1x" className="text-brand-300"/>
                            </button>
                            <VideoAssetFormModal
                                modalId={`videoAssetModal-${content.id}`}
                                openModal={openVideoAssetEditModal}
                                onClose={() => setVideoAssetEditModal(false)}
                                content={content}
                            />
                        </>
                    }

                </div>
                {renderVideoAsset(content.videoAsset)}
            </Card>
        );
    };

    const renderBookmark = (
            bookmark:  Bookmark | null | undefined,
            title: string
        ) =>{

        return (
            <>
                {
                    bookmark &&
                    <>
                        <p className={`font-bold`}>{title}</p>
                        <div className={`col-span-2 grid grid-cols-subgrid gap-2`}>
                            {
                                bookmark.startOffsetMS !== null &&
                                bookmark.startOffsetMS !== undefined &&
                                <p>{dayjs.duration(bookmark.startOffsetMS).format("HH:mm:ss")}</p>
                            }
                            {
                                bookmark.endOffsetMS !== null &&
                                bookmark.endOffsetMS !== undefined &&
                                <p>{dayjs.duration(bookmark.endOffsetMS).format("HH:mm:ss")}</p>
                            }
                        </div>
                    </>
                }
            </>

        );
    };

    const renderBookmarks = (content: Content) => {
        return (
            <Card className="px-8 py-4">
                <h2 className="text-lg pb-2 mb-4 border-b-2 border-b-brand-500 dark:border-b-white">
                    <span className="font-bold mr-6">
                        <FontAwesomeIcon icon={faBookBookmark} className="mx-4"/>
                        Bookmarks
                    </span>
                </h2>
                {
                    (!content.videoAsset
                    || !content.videoAsset.bookmarks) || (
                    (content.videoAsset.bookmarks.intro1 === null
                    || content.videoAsset.bookmarks.intro1 == undefined)  &&
                    (content.videoAsset.bookmarks.intro2 === null
                    || content.videoAsset.bookmarks.intro2 == undefined) &&
                    (content.videoAsset.bookmarks.closingCredits === null
                    || content.videoAsset.bookmarks.closingCredits == undefined)) ?
                    <div className={`w-full h-full flex justify-center items-center`}>
                        <p className={`text-gray-500 font-bold py-6`}>Not Available</p>
                    </div> :
                    <div className = {`grid grid-cols-3 gap-x-2 gap-y-3`}>
                        <p className = {`col-start-2 font-bold`}>Start Offset</p>
                        <p className = {`font-bold`}>End Offset</p>
                        {renderBookmark(content.videoAsset.bookmarks.intro1, "Intro 1")}
                        {renderBookmark(content.videoAsset.bookmarks.intro2, "Intro 2")}
                        {renderBookmark(content.videoAsset.bookmarks.closingCredits, "Closing Credits")}
                    </div>
                }
            </Card>
        );
    };

    const renderBreakpoints = (content: Content) => {
        return (
            <Card className="px-8 py-4">
                <h2 className="text-lg pb-2 mb-4 border-b-2 border-b-brand-500 dark:border-b-white">
                    <span className="font-bold mr-6">
                        <FontAwesomeIcon icon={faRectangleAd} className="mx-4"/>
                        Breakpoints
                    </span>
                </h2>
                <div>
                    {
                        !content.videoAsset
                        || !content.videoAsset.breakPositions
                        || content.videoAsset.breakPositions.length === 0 ?
                         <div className={`w-full h-full flex justify-center items-center`}>
                            <p className={`text-gray-500 font-bold py-6`}>Not Available</p>
                         </div> :
                        content.videoAsset.breakPositions?.map((breakPosition, index) => {
                            return (
                                <div key={breakPosition.breakOutOffsetMS} className={`grid grid-cols-2 gap-2 items-center`}>
                                    <div className="flex flex-row justify-end">
                                        <p className={`text-right`}>{`Breakpoint `}</p>
                                        <p className={`w-4 text-right`}>{`${index + 1}: `}</p>
                                    </div>
                                    {
                                        breakPosition.breakOutOffsetMS &&
                                        <p>
                                            {
                                                dayjs.duration(breakPosition.breakOutOffsetMS)
                                                    .format(("HH:mm:ss"))
                                            }
                                        </p>
                                    }
                                </div>
                            );
                        })
                    }
                </div>
            </Card>
        );
    };

    const renderSubtitleAssets = () => {
        // TODO
        return (
            <Card className="px-8 py-4">
                <h2 className="text-lg pb-2 mb-4 border-b-2 border-b-brand-500 dark:border-b-white">
                    <span className="font-bold mr-6">
                        <FontAwesomeIcon icon={faRectangleAd} className="mx-4"/>
                        Subtitles
                    </span>
                </h2>
                <div>
                    <div className={`w-full h-full flex justify-center items-center`}>
                        <p className={`text-gray-500 font-bold py-6`}>Not Available</p>
                    </div>
                </div>
            </Card>
        );
    };

    return content
     ? (
        <>
            <VODAdminLayout
                heading={`${content.contentType ? ContentTypeLabels[content.contentType] : "VOD"} Content`}
                subHeading={
                    <div className="mt-4">
                        {renderNavigation(boxSetContentGroup, seriesContentGroup, seasonContentGroup, content)}
                    </div>
                }
            >
                <div className="flex flex-col mr-6">
                    <div className="grid grid-cols-12 grid-rows-[auto] gap-4 mb-10">
                        <div className="col-span-8 row-start-2">
                            {renderMainMetadata(seriesContentGroup, seasonContentGroup, content)}
                        </div>
                        <div className="col-span-4 col-start-9 row-start-2">
                            {renderReferences(content)}
                        </div>
                        <div className="col-span-12 row-start-3">
                            {renderScheduling(content)}
                        </div>
                        <div className="col-span-12 row-start-4">
                            {renderImages(content)}
                        </div>
                        <div className="col-span-4 row-start-5">
                            {renderVideoAssets(content)}
                        </div>
                        <div className="col-span-4 col-start-5 row-start-5">
                            {renderPreviewVideoAssets(content)}
                        </div>
                        <div className="col-span-4 col-start-9 row-start-5">
                            {renderSubtitleAssets()}
                        </div>
                        <div className="col-span-4 row-start-6">
                            {renderBreakpoints(content)}
                        </div>
                        <div className="col-span-4 col-start-5 row-start-6">
                            {renderBookmarks(content)}
                        </div>
                    </div>
                    <div className="p-10"></div>
                </div>
            </VODAdminLayout>
        </>
    ) : (
        <></>
    );
};

export default VODContentView;